import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../../commonComponents/header/Header.js";
import { authService } from "../../../services/apiInterviewService";
import CommonSnackbar from "../../commonComponents/snackbar/CommonSnackbar.js";
import Footer from "../../commonComponents/footer/Footer.js";
import LoadingDialog from "../../commonComponents/loader/LoadingDialog.js";
import { motion } from "framer-motion";
import {
  maskPhone,
  maskPhoneNumber,
  maskText,
} from "../../commonComponents/helperFunctions/HelperFunction.js";
import "./login.css";

// Images
import captchaVerificationImg from "../../../assets/images/nh_consumer_human_verification.png";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import backArrowImg from "../../../assets/images/icon_back_to_previous.svg";
import backSpace from "../../../assets/images/backspace-icon.svg";
import crossMark from "../../../assets/images/cross-mark.svg";
import "./login.css";
import { comma } from "postcss/lib/list";
import { getJdId } from "../../../utils/decryptURL.js";

const CaptchaVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [flowType, setFlowType] = useState("regular"); // "regular" or "screenup"

  // Captcha related states
  const [captchaCode, setCaptchaCode] = useState("");
  const [captchaInput, setCaptchaInput] = useState(["", "", "", "", "", ""]);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
  const [dialpadNumbers, setDialpadNumbers] = useState([
    1, 2, 3, 4, 5, 6, 7, 8, 9, 0,
  ]);

  useEffect(() => {
    // Clear any previous flow state
    setEmail("");
    setPhoneNumber("");
    setFlowType("regular");

    // Determine flow type - priority: location state > session storage > default
    const urlFlowType = location.state?.flowType;
    const sessionFlowType = sessionStorage.getItem("flow_type");
    let currentFlowType = "regular";

    if (urlFlowType === "screenup") {
      currentFlowType = "screenup";
    } else if (sessionFlowType === "screenup") {
      currentFlowType = "screenup";
    }

    setFlowType(currentFlowType);

    if (currentFlowType === "screenup") {
      // For screenup flow, get email from location state or sessionStorage
      const storedEmail =
        location.state?.email || sessionStorage.getItem("screenup_email");

      if (!storedEmail) {
        setSnackbar({
          message: "Session expired. Please login again.",
          type: "failure",
        });
        setTimeout(() => navigate("/login"), 2000);
        return;
      }
      setEmail(storedEmail);
    } else {
      // For regular flow, get both email and phone
      const storedEmail = sessionStorage.getItem("login_email");
      const storedPhone = sessionStorage.getItem("login_phone");

      if (!storedEmail || !storedPhone) {
        setSnackbar({
          message: "Session expired. Please login again.",
          type: "failure",
        });
        setTimeout(() => navigate("/screenup-login"), 2000);
        return;
      }
      setEmail(storedEmail);
      setPhoneNumber(storedPhone);
    }

    generateCaptcha();
  }, [navigate, location.state]);

  // Generate random 6-digit captcha
  const generateCaptcha = () => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    setCaptchaCode(code);
    setCaptchaInput(["", "", "", "", "", ""]);
    setIsCaptchaVerified(false);
    // Shuffle dialpad numbers without immediate effect
    shuffleDialpadSmooth();

    // Show snackbar for new captcha generation
    setSnackbar({
      message: "New captcha generated successfully!",
      type: "success",
    });
  };

  // Smooth shuffle dialpad numbers to prevent jumping
  const shuffleDialpadSmooth = () => {
    // Use setTimeout to prevent layout jump during state updates
    setTimeout(() => {
      const numbers = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
      for (let i = numbers.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
      }
      setDialpadNumbers(numbers);
    }, 50);
  };

  // Handle captcha input change
  const handleCaptchaInputChange = (index, value) => {
    const newInput = [...captchaInput];
    newInput[index] = value;
    setCaptchaInput(newInput);

    // Verification check
    if (newInput.every((digit) => digit !== "")) {
      const enteredCode = newInput.join("");
      if (enteredCode === captchaCode) {
        setIsCaptchaVerified(true);
        setSnackbar({
          message: "Verification completed successfully.",
          type: "success",
        });
      } else {
        setIsCaptchaVerified(false);
        setSnackbar({
          message: "Incorrect captcha. Please try again.",
          type: "failure",
        });
        // Auto-clear input after wrong captcha
        setTimeout(() => {
          setCaptchaInput(["", "", "", "", "", ""]);
        }, 1000);
      }
    } else {
      setIsCaptchaVerified(false);
    }
  };

  // Handle dialpad click with smooth shuffle
  const handleDialpadClick = (digit) => {
    const currentIndex = captchaInput.findIndex((val) => val === "");
    if (currentIndex !== -1) {
      handleCaptchaInputChange(currentIndex, digit.toString());
      // Delay shuffle to prevent jumping
      shuffleDialpadSmooth();
    }
  };

  // Handle backspace on dialpad
  const handleBackspace = () => {
    let lastFilledIndex = -1;
    for (let i = captchaInput.length - 1; i >= 0; i--) {
      if (captchaInput[i] !== "") {
        lastFilledIndex = i;
        break;
      }
    }
    if (lastFilledIndex !== -1) {
      handleCaptchaInputChange(lastFilledIndex, "");
    }
  };

  // Handle back button
  const handleBack = () => {
    if (flowType === "screenup") {
      navigate("/login");
    } else {
      navigate("/screenup-login");
    }
  };

  // Handle submit based on flow type
  const handleSubmit = async () => {
    const companyId = sessionStorage.getItem("companyId") || "";
    if (!isCaptchaVerified) {
      setSnackbar({
        message: "Please verify the captcha before proceeding.",
        type: "failure",
      });
      return;
    }

    setIsLoading(true);
    setSnackbar(null);

    try {
      if (flowType === "screenup") {
        // For screenup flow, send OTP to email only
        const jd_id=getJdId();
        const response = await fetch(
          `${process.env.REACT_APP_API_ROOT_URL}/auth/api/v1/Authentication/screenup/send-otp`,
          {
            method: "POST",
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: email,
              company_id: companyId,
              jd_id

            }),
          },
        );

        if (response.ok) {
          const data = await response.json();

          setSnackbar({
            message: "OTP sent successfully to your email!",
            type: "success",
          });

          // Navigate to screenup OTP page
          setTimeout(() => {
            navigate("/otp", { state: { email: email } });
          }, 1500);
        } else {
          const errorData = await response.json();
          console.error("API Error:", errorData);
          setSnackbar({
            message:
              errorData.message ||
              errorData?.detail ||
              "Failed to send OTP. Please try again.",
            type: "failure",
          });
        }
      } else {
        // For regular flow, use existing authService
        const response = await authService.sendOTP(email, phoneNumber);

        setSnackbar({
          message: "OTPs sent successfully to your email and phone!",
          type: "success",
        });

        // Navigate to regular OTP page
        setTimeout(() => {
          navigate("/screenup-otp");
        }, 1500);
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setSnackbar({
        message: error.message || "Failed to send OTP(s). Please try again.",
        type: "failure",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getLoadingMessage = () => {
    if (flowType === "screenup") {
      return (
        <>
          Sending OTP to your Email:{" "}
          <span className="font-semibold">{maskText(email)}</span>...
        </>
      );
    }

    return (
      <>
        Sending One Time PINs (OTP) to your Email:{" "}
        <span className="loading-highlight">{maskText(email)}</span> and Phone:
        {" +"}
        <span className="loading-highlight">{maskPhone(phoneNumber)}</span>...
        This will be quick.
      </>
    );
  };

  const getHeaderTitle = () => {
    if (flowType === "screenup") {
      return "Human Verification";
    }
    return "Human Verification";
  };

  const getSubtitle = () => {
    return "Using the dialpad, click the numbers shown in the picture.";
  };

  return (
    <div className="min-h-screen flex flex-col bg-white pt-4 px-4">
      {isLoading && (
        <LoadingDialog
          title="Sending Verification Code"
          primaryMSG={
            flowType === "screenup"
              ? "Sending OTP to your"
              : " Sending One Time PINs (OTP) to your"
          }
          email={
            flowType === "screenup" ? (
              <span className="loading-highlight">{maskText(email)}</span>
            ) : (
              <span className="loading-highlight">{maskText(email)}</span>
            )
          }
          phone={
            flowType === "screenup" ? (
              ""
            ) : (
              <span className="loading-highlight">
                {maskPhoneNumber(phoneNumber)}
              </span>
            )
          }
          bottomMsg="This will be quick."
        />

        //  <span className="loading-highlight">{maskText(email)}</span> and Phone:
        // {" +"}
        // <span className="loading-highlight">{maskPhone(phoneNumber)}</span>...
        // This will be quick.
      )}
      {snackbar && (
        <CommonSnackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={() => setSnackbar(null)}
        />
      )}

      <Header />

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row overflow-y-hidden ">
        {/* Left Image - Updated to use different image */}
        <div className="w-full md:w-1/2 flex justify-center items-center pl-[34px] pr-[50px] ">
          <img
            src={captchaVerificationImg}
            alt="captcha verification"
            className="object-contain h-[auto] w-[85%] rounded-3xl"
          />
        </div>

        {/* Divider (desktop only) */}
        <div className="hidden md:block border-l border-dividerColor my-[5%]" />

        {/* Right Form Section */}
        <div className="w-full md:w-1/2 flex flex-col justify-center  md:overflow-y-auto custom-scrollbar mt-10  px-[50px] text-center overflow-x-hidden">
          <div className="w-full md:w-[100%] ">
            <div className="flex flex-col items-center mb-0">
              <div className="flex items-center w-full max-w-xl">
                <h2 className="text-loginHeaderFontSize font-[500] text-fontColor ml-2">
                  {getHeaderTitle()}
                </h2>
              </div>

              {/* Subtitle */}
              <p className="text-primaryFontSize text-fontColor mt-1 w-full max-w-xl text-left">
                {getSubtitle()}
              </p>
            </div>
            {/* Captcha Section */}
            <div className="mt-8">
              <div className="text-center mb-6">
                {/* Captcha Display */}
                <div className="relative inline-block">
                  <div className="rounded-lg p-4 mb-3 shadow-sm border">
                    <div className="text-loginHeaderFontSize font-bold text-fontColor tracking-[0.3em] font-mono">
                      {captchaCode}
                    </div>
                    <div className="absolute top-2 right-2">
                      <button
                        type="button"
                        onClick={generateCaptcha}
                        className="text-logoColor hover:text-opacity-80 transition-colors duration-200 p-1"
                        title="Refresh Captcha"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M4 12a8 8 0 0 1 7.89-8 8.18 8.18 0 0 1 .5 0 8 8 0 0 1 7.61 8c0 .29-.02.58-.05.86L18 11l1.35-1.32c.08.77.15 1.49.15 2.32a10 10 0 0 1-9.5 10A10 10 0 0 1 2 12c0-1.68.41-3.26 1.14-4.65L5 9z" />
                          <path d="M18 4.26v1.8L16.15 7.9A8 8 0 0 0 12 4a8.08 8.08 0 0 0-6.84 3.84L3.32 6A10 10 0 0 1 12 2a9.77 9.77 0 0 1 8 4.26z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Captcha Input Boxes */}
              <div className="flex justify-center gap-3 mb-6">
                {captchaInput.map((value, index) => (
                  <div key={index} className="relative">
                    <input
                      readOnly
                      id={`captcha-input-${index}`}
                      type="text"
                      value={value}
                      onChange={(e) =>
                        handleCaptchaInputChange(index, e.target.value)
                      }
                      className={`w-12 h-12 border-2 rounded-lg text-primaryFontSize text-center text-lg font-semibold focus:outline-none transition-all duration-200 ${
                        value
                          ? isCaptchaVerified
                            ? "border-green-500 bg-green-50 text-green-700"
                            : "border-logoColor bg-blue-50 text-fontColor"
                          : "captcha-input-box bg-white text-fontColor hover:border-logoColor"
                      }`}
                      maxLength="1"
                    />
                    {value && isCaptchaVerified && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 24 24"
                          fill="white"
                        >
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Enhanced Dialpad - Fixed to prevent jumping */}
              <div className="bg-gray-50 border rounded-lg p-4 shadow-sm-new">
                <div className="grid grid-cols-3 gap-2 max-w-[180px] mx-auto">
                  {/* Simplified dialpad without AnimatePresence to prevent jumping */}
                  {dialpadNumbers.slice(0, 9).map((digit, index) => (
                    <motion.button
                      key={`${digit}-${index}`} // Stable key to prevent remounting
                      type="button"
                      onClick={() => handleDialpadClick(digit)}
                      className="w-14 h-14 border-2 border-logoColor rounded-lg bg-white text-primaryFontSize text-fontColor text-lg font-semibold 
                       hover:text-overFontSize focus:outline-none focus:ring-2 focus:ring-logoColor focus:ring-opacity-50 transition-all duration-200
                        hover:scale-105 active:scale-95 active:bg-[#111729] active:text-white hover:bg-[#111729] hover:text-white"
                      // Removed layout animation and reduced motion complexity
                      whileTap={{ scale: 0.95 }}
                      transition={{ duration: 0.1 }}
                    >
                      {digit}
                    </motion.button>
                  ))}

                  {/* Bottom row with 0 and backspace */}
                  <div></div>
                  <motion.button
                    key={`${dialpadNumbers[9]}-9`} // Stable key
                    type="button"
                    onClick={() => handleDialpadClick(dialpadNumbers[9])}
                    className="w-14 h-14 border-2 border-logoColor rounded-lg bg-white text-fontColor text-lg font-semibold  hover:text-overFontSize focus:outline-none focus:ring-2 focus:ring-logoColor focus:ring-opacity-50 transition-all duration-200 transform hover:scale-105 active:scale-95 active:bg-[#111729] active:text-white hover:bg-[#111729] hover:text-white"
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.1 }}
                  >
                    {dialpadNumbers[9]}
                  </motion.button>

                  <motion.button
                    key="backspace"
                    type="button"
                    onClick={handleBackspace}
                    className="w-14 h-14 border-2 border-red-400 rounded-lg bg-white text-red-500 text-lg font-bold hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-sm backspace-bg"
                    whileTap={{ scale: 0.9 }}
                  >
                    <img
                      src={backSpace}
                      alt="Backspace"
                      className="w-5 h-5 mx-auto"
                    />
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            {/* <div className="flex justify-end w-full "> */}
          </div>
          <div className="mt-auto flex justify-end pb-4">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || !isCaptchaVerified}
              className="flex btn-size items-center px-6 py-1.5  bg-btnBgColor text-white transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed mr-[36px]"
            >
              <span className="btn-size">Proceed</span>
              {/* Conditionally render arrow only when button is enabled */}
              <img src={arrowImg} alt="arrow" className="ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CaptchaVerification;
